// Stage Mail-3:MailDetail(中パネル)。
//
// MailDetail = 右中央メールの本文表示 + status / is_read / is_important の
// PATCH ボタン + (Mail-4A 用) 返信ボタン枠。
//
// 仕様(design §5.3):
// - GET /api/mail/messages/<id> を叩いて詳細取得
// - 取得時 / PATCH 後に is_read=true を自動設定(design は Mail-4A 範囲外だが
//   UI 操作上、未読が開かれると既読化する動作は妥当)
// - PATCH ボタンは system_admin / editor のみ閲覧可能 + 操作可能
// - viewer は status / is_read / is_important の PATCH ボタン非表示
//   (MarkdownMail-3 §5.1 に「viewer PATCH → 403」、UI からも呼べない)
// - メール本文は bleach で body_html_sanitized されている前提で
//   innerHTML 経由表示(Mail-2 で strip 済み → XSS 安全)
// - ただし Mail-3 では防衛的に再度 sanitize はしない(production bleach
//   側で完結)

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Star, Mail, MailOpen, Reply, Archive, Trash2, Sparkles } from "lucide-react";
import { mailApi, type MailMessage, type MailStatus } from "../../api/mail";
import { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { MailStatusBadge } from "./MailStatusBadge";
import { formatJstDateTime } from "../../utils/dateTime";

const STATUS_OPTIONS: MailStatus[] = ["unhandled", "handling", "completed"];

export interface MailDetailProps {
  messageId: string | null;
  /** 親の更新関数 — PATCH / 削除後に list 再読込に使われる */
  onChanged?: () => void;
}

export function MailDetail({ messageId, onChanged }: MailDetailProps) {
  const { hasAnyRole } = useAuth();
  const canPatch = hasAnyRole(["system_admin", "editor"]);

  const [m, setM] = useState<MailMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"status" | "read" | "important" | "ai" | null>(null);

  // Mail-4B: capability endpoint を 叩いて ai_reply_enabled を 取得。
  // false の場合 AI 返信 ボタン を 描画 し ない(design §7.3 二重 検証)。
  // ※ MailDetail の マウント 中 のみ 取得(reply 中 に 動的 通信 は 不要)。
  const [aiReplyEnabled, setAiReplyEnabled] = useState<boolean>(false);
  useEffect(() => {
    let alive = true;
    if (!canPatch) {
      setAiReplyEnabled(false);
      return;
    }
    (async () => {
      try {
        const cap = await mailApi.getCapability();
        if (alive) setAiReplyEnabled(Boolean(cap.ai_reply_enabled));
      } catch {
        if (alive) setAiReplyEnabled(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [canPatch]);

  // ── Mail-4B: AI 返信生成 ──
  // design §7.3「フロント Hidden ≠ サーバ 放行」:
  // - ボタン 自体 が canPatch と aiReplyEnabled の AND で 表示
  // - viewer は backend が 403 を返す が、 そもそも 表示 しない
  const [aiDraft, setAiDraft] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const handleAiReply = useCallback(async () => {
    if (!m) return;
    setBusy("ai");
    setAiError(null);
    setAiDraft(null);
    try {
      const result = await mailApi.aiReply(m.id, { tone: "polite", thread_limit: 5 });
      setAiDraft(result.draft_text || "");
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setAiError(msg);
    } finally {
      setBusy(null);
    }
  }, [m]);

  const markReadRef = useRef<boolean>(false);

  const load = useCallback(async () => {
    if (!messageId) {
      setM(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await mailApi.getMessage(messageId);
      setM(result);
      // 詳細を開いたら未読 → 既読に自動切替(Mail-2 範囲の妥協動作、
      // Mail-3 では viewer でも実行。PATCH 権限不要 — backend は GET でも
      // state を変えてはいけないが、UI 操作 1 アクション程度の rational)。
      // ただし viewer は PATCH が 403 なので、ここでは is_read 自動切替を
      // skip して manual 操作だけにする。
      if (!result.is_read && !markReadRef.current) {
        markReadRef.current = true;
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setM(null);
      } else {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    markReadRef.current = false;
    load();
  }, [load]);

  async function patchStatus(s: MailStatus) {
    if (!m || !canPatch) return;
    setBusy("status");
    try {
      const updated = await mailApi.patchMessage(m.id, { status: s });
      setM(updated);
      onChanged?.();
    } catch (e) {
      window.alert(`status 更新失敗: ${e instanceof ApiError ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  async function patchRead(target: boolean) {
    if (!m || !canPatch) return;
    setBusy("read");
    try {
      const updated = await mailApi.patchMessage(m.id, { is_read: target });
      setM(updated);
      onChanged?.();
    } catch (e) {
      window.alert(`既読更新失敗: ${e instanceof ApiError ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  async function patchImportant(target: boolean) {
    if (!m || !canPatch) return;
    setBusy("important");
    try {
      const updated = await mailApi.patchMessage(m.id, { is_important: target });
      setM(updated);
      onChanged?.();
    } catch (e) {
      window.alert(`重要更新失敗: ${e instanceof ApiError ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  if (!messageId) {
    return (
      <Card>
        <div
          style={{
            padding: 48,
            textAlign: "center",
            color: "#9ca3af",
            fontSize: 14,
          }}
        >
          左の一覧からメールを選択してください
        </div>
      </Card>
    );
  }

  if (loading && !m) {
    return (
      <Card>
        <div style={{ padding: 48, textAlign: "center", color: "#6b7280" }}>
          読み込み中...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div
          style={{
            padding: 16,
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
          }}
        >
          {error}
        </div>
      </Card>
    );
  }

  if (!m) {
    return (
      <Card>
        <div style={{ padding: 48, textAlign: "center", color: "#6b7280" }}>
          メールが見つかりません
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="row-between mb-18">
        <div className="row gap-8">
          {canPatch && (
            <>
              <Button
                variant="ghost"
                iconLeft={<MailOpen size={13} />}
                onClick={() => patchRead(true)}
                disabled={busy !== null || m.is_read}
                title="既読にする"
              >
                既読化
              </Button>
              <Button
                variant="ghost"
                iconLeft={<Mail size={13} />}
                onClick={() => patchRead(false)}
                disabled={busy !== null || !m.is_read}
                title="未読にする"
              >
                未読化
              </Button>
              <Button
                variant="ghost"
                iconLeft={<Star size={13} fill={m.is_important ? "#f59e0b" : "none"} />}
                onClick={() => patchImportant(!m.is_important)}
                disabled={busy !== null}
                title={m.is_important ? "重要解除" : "重要にする"}
              >
                {m.is_important ? "重要解除" : "重要"}
              </Button>
            </>
          )}
          {/* Mail-4A 范围 — Mail-3 で枠だけ表示、操作不可 */}
          <Button
            variant="ghost"
            iconLeft={<Reply size={13} />}
            disabled
            title="Mail-4A で実装予定"
          >
            返信
          </Button>
          {/* Mail-4B: AI 返信ドラフト(design §7.3 二重 検証 + §7.9 統合):
              - capability off → ボタン 非表示
              - viewer / editor 以外 → 非表示
              - 押すと ai-reply endpoint 呼び、結果を 下 の textarea に 注入
                (Mail-4A の 送信 経路 とは 連動 しない、Mail-3 frontend 範囲) */}
          {canPatch && aiReplyEnabled && (
            <Button
              variant="secondary"
              iconLeft={<Sparkles size={13} />}
              disabled={busy === "ai" || m.direction !== "inbound"}
              onClick={handleAiReply}
              title="AI が返信ドラフトを生成"
            >
              AI 返信文を作成
            </Button>
          )}
          <Button variant="ghost" iconLeft={<Archive size={13} />} disabled title="Mail-3 範囲外">
            アーカイブ
          </Button>
          <Button variant="ghost" iconLeft={<Trash2 size={13} />} disabled title="Mail-3 範囲外">
            削除
          </Button>
        </div>
        <div className="row gap-8">
          <MailStatusBadge status={m.status} />
          {m.direction === "outbound" && <Badge color="blue">送信</Badge>}
          {m.is_important && <Badge color="yellow">重要</Badge>}
        </div>
      </div>

      <div className="mail-detail-head">
        <div className="mail-detail-subject">{m.subject || "(無題)"}</div>
        <div className="mail-detail-from">
          <div
            className="avatar avatar-lg"
            style={{
              background:
                m.from_name && m.from_name.length > 0
                  ? "linear-gradient(135deg, #3b82f6, #6366f1)"
                  : "#9ca3af",
              color: "#fff",
            }}
          >
            {(m.from_name || m.from_email).charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
              {m.from_name || "(名前なし)"}{" "}
              <span style={{ color: "#6b7280", fontWeight: 400 }}>
                &lt;{m.from_email}&gt;
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              宛先: {m.to_addresses} {m.date_header && " · "}
              {m.date_header}
            </div>
          </div>
        </div>
      </div>

      <div className="mail-body">
        {m.body_html_sanitized ? (
          // Stage Mail-2+ 修正: HTML メール を plain text より 優先 する。
          // 旧 実装 は body_text 優先 だっ た ため、 マーケティング メール /
          // Outlook 通知 等 の 排版 が 失わ れ て い た。
          // body_html_sanitized は Mail-2 の bleach.clean が
          // <script>/<iframe>/<object>/<embed>/<form>/on* event handler /
          // javascript: URL を 剥落 / 禁則 し た 結果。
          <div dangerouslySetInnerHTML={{ __html: m.body_html_sanitized }} />
        ) : m.body_text ? (
          m.body_text.split("\n").map((line, i) => (
            <div
              key={i}
              style={{ marginBottom: line.trim() === "" ? 8 : 0 }}
            >
              {line}
            </div>
          ))
        ) : (
          <span style={{ color: "#9ca3af" }}>(本文なし)</span>
        )}
      </div>

      {/* status PATCH 行 */}
      {canPatch && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 14,
            borderTop: "1px dashed #e5e7eb",
          }}
        >
          <div
            style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}
          >
            対応ステータス:
          </div>
          <div className="row gap-8">
            {STATUS_OPTIONS.map((s) => (
              <Button
                key={s}
                variant={m.status === s ? "primary" : "secondary"}
                disabled={busy !== null}
                onClick={() => patchStatus(s)}
              >
                {s === "unhandled" ? "未対応" : s === "handling" ? "対応中" : "対応済み"}
              </Button>
            ))}
          </div>
          {m.received_at && (
            <div
              style={{
                marginTop: 10,
                fontSize: 11,
                color: "#9ca3af",
              }}
            >
              受信: {formatJstDateTime(m.received_at)}  ·  msg-id: {m.message_id || "(なし)"}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
